import { Temporal } from 'temporal-polyfill';

export const TIMEZONE_LOCAL = 'local';
export const TIMEZONE_UTC = 'UTC';

export function getLocalTimezone(): string {
	return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function resolveTimezone(timezone: string): string {
	if (timezone === TIMEZONE_LOCAL) {
		return getLocalTimezone();
	}

	return timezone;
}

export function isValidTimezone(timezone: string): boolean {
	try {
		const resolved = resolveTimezone(timezone);
		const formatter = new Intl.DateTimeFormat('en-US', { timeZone: resolved });
		return formatter.resolvedOptions().timeZone !== undefined;
	}
	catch {
		return false;
	}
}

export function toZonedDateTime(
	dateTime: Temporal.PlainDateTime,
	timezone: string,
): Temporal.ZonedDateTime {
	// Note: temporal-polyfill uses 'compatible' disambiguation by default
	// During DST gaps, ambiguous times are resolved to the later valid time
	return dateTime.toZonedDateTime(resolveTimezone(timezone));
}

export function nowInTimezone(timezone: string): Temporal.ZonedDateTime {
	return Temporal.Now.zonedDateTimeISO(resolveTimezone(timezone));
}

/**
 * Formats a timezone offset as a string (e.g., "+02:00", "-05:00", "+00:00")
 */
export function formatTimezoneOffset(timezone: string, instant?: Temporal.Instant): string {
	const resolvedTimezone = resolveTimezone(timezone);
	const atInstant = instant ?? Temporal.Now.instant();
	const zoned = atInstant.toZonedDateTimeISO(resolvedTimezone);

	return zoned.offset;
}

/**
 * Formats a timezone as a short label (e.g., "PST", "EST", "UTC")
 * Falls back to offset format if short name is not available
 */
export function formatTimezoneLabel(
	timezone: string,
	dateTime?: Temporal.PlainDateTime,
	style: 'short' | 'long' | 'shortOffset' | 'longOffset' = 'short',
	locale = 'en-US',
): string {
	const resolvedTimezone = resolveTimezone(timezone);

	if (resolvedTimezone === TIMEZONE_UTC) {
		return 'UTC';
	}

	const refDateTime = dateTime ?? Temporal.Now.plainDateTimeISO();
	const zonedDateTime = refDateTime.toZonedDateTime(resolvedTimezone);

	try {
		const formatter = new Intl.DateTimeFormat(locale, {
			timeZone: resolvedTimezone,
			timeZoneName: style,
		});

		const parts = formatter.formatToParts(new Date(Number(zonedDateTime.epochMilliseconds)));
		const timeZonePart = parts.find((part) => part.type === 'timeZoneName');

		if (timeZonePart) {
			return timeZonePart.value;
		}
	}
	catch {
		console.error(`Invalid timezone: ${resolvedTimezone}`);
	}

	return zonedDateTime.offset;
}

export function generateTimezoneTooltip(
	dateTime: Temporal.PlainDateTime | null,
	currentTimezone: string,
	locale = 'en-US',
): string | null {
	if (!dateTime) return null;

	const resolvedCurrentTz = resolveTimezone(currentTimezone);

	if (resolvedCurrentTz === TIMEZONE_UTC) {
		return null;
	}

	const zonedInCurrent = dateTime.toZonedDateTime(resolvedCurrentTz);
	const instant = zonedInCurrent.toInstant();

	const formatOptions: Intl.DateTimeFormatOptions = {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false,
	};

	const utcFormatted = new Intl.DateTimeFormat(locale, {
		...formatOptions,
		timeZone: TIMEZONE_UTC,
	}).format(new Date(Number(instant.epochMilliseconds)));

	return utcFormatted;
}

export function getCommonTimezones(): Array<{ id: string; label: string; offset: string }> {
	const instant = Temporal.Now.instant();
	const commonZones = [
		'UTC',
		'America/New_York',
		'America/Chicago',
		'America/Denver',
		'America/Los_Angeles',
		'America/Anchorage',
		'Pacific/Honolulu',
		'Europe/London',
		'Europe/Paris',
		'Europe/Berlin',
		'Europe/Moscow',
		'Asia/Dubai',
		'Asia/Kolkata',
		'Asia/Singapore',
		'Asia/Tokyo',
		'Asia/Shanghai',
		'Australia/Sydney',
		'Pacific/Auckland',
	];

	return commonZones.map((id) => ({
		id,
		label: id,
		offset: formatTimezoneOffset(id, instant),
	}));
}

export function getTimezoneSelectOptions(): Array<{ label: string; value: string }> {
	return [
		{
			label: '$t:field_types.datetime.options.local',
			value: TIMEZONE_LOCAL,
		},
		...getCommonTimezones().map(({ id, label, offset }) => ({
			label: `${label} (${offset})`,
			value: id,
		})),
	];
}
